import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useClubNetworkFeed } from "../../hooks/club-network/useClubNetworkFeed";
import { useViewMode } from "../../hooks/useViewMode";
import ClubRequestCard from "../../components/club-network/ClubRequestCard";
import AgentRequestListRow from "../../components/club-network/AgentRequestListRow";
import ClubNetworkFilterModal from "../../components/club-network/ClubNetworkFilterModal";
import MessageAgentModal from "../../components/club-network/MessageAgentModal";
import CnFilterDropdown from "../../components/club-network/v2/CnFilterDropdown";
import { MagnifierGlyph } from "../../components/club-network/v2/cnGlyphs";
import { RemovableFilterChip } from "../../components/club-network/v2/cnPills";
import FlagImg from "../../components/agent-tender/FlagImg";
import { BookmarkGlyph } from "../../components/agent-tender/glyphs";
import CategoryPills from "../../components/agent-tender/CategoryPills";
import CustomDropdown from "../../components/common/CustomDropdown";
import v2DropdownProps from "../../components/agent-tender/v2DropdownProps";
import {
  formatPositionValue,
  getPositionsForCategory,
  FEED_LEVEL_CHOICES,
  FEED_TYPE_FILTER_CHOICES,
} from "../../constants/agentTenderChoices";

// =====================================================================
// N2-1 — Agent Requests feed, club side (Redesign 2.0 boards n2-1 / n2-1f /
// n2-1e / n2-1s + mobile board M1 @390px). Presentation layer only: ALL
// state/behavior stays in useClubNetworkFeed and is untouched (brief Rule 6/7).
//
// Artboard grammar (N2-1, 920px canvas → real shell per Rule 5):
//   page head (18px/700 title + anonymity subline 12.5px #6B7280) ·
//   toolbar = search field (white fill, verbatim S3 magnifier, "Search
//   position, country, agency…") + filter dropdown buttons Position /
//   Country / Request type (§1.9 recipe; the artboard's "Tender type" label
//   ships as "Request type" per the terminology rule; its Window filter has
//   NO backend param → not wired, FUNCTION-TODO) · count line "N requests ·
//   verified first" · 2-col card grid (real widths follow the shipped V2
//   card grid — §9.3-C) · N2-1f removable chips + "Clear all" · N2-1e empty
//   card · N2-1s static skeleton bones (#EEF0F3/#F3F4F6, second row faded).
// M1 (binding, 390px): white header band with 16px title, gray search field
//   (r10), horizontally scrollable filter pills "Position ▾ Country ▾
//   Type ▾", single-column cards, no top-bar chrome (platform shell rules).
//
// Existing functions WITHOUT an artboard placement are kept wired at best
// fit and flagged OPEN in PARITY-B1.md (Rule 7): category pills, Verified-
// only, Level, Sort, Saved toggle, Filters modal, card/list toggle,
// pagination, error state.
// =====================================================================

const SORT_CHOICES = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "window_soonest", label: "Window: soonest" },
];

// ── Verbatim V2 vocabulary glyphs (agent-feed toolbar recipe) ────────
const FunnelGlyph = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 5h16l-6 7v5l-4 2v-7z" />
  </svg>
);

const GridGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const ListGlyph = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

// Cards/List toggle — V2 vocabulary (two 30px icon cells). Same handler as
// before (useViewMode); visible at every width so list mode is never stranded.
const FeedViewToggle = ({ value, onChange }) => {
  const cell = (mode, glyph, label) => {
    const isActive = (value === "list" ? "list" : "card") === mode;
    return (
      <button
        type="button"
        onClick={() => onChange(mode)}
        aria-pressed={isActive}
        aria-label={label}
        title={label}
        className={`w-[30px] h-[30px] inline-flex items-center justify-center ${
          isActive ? "bg-[#E8F1FF] text-[#1B6FE0]" : "bg-white text-[#64748B]"
        }`}
      >
        {glyph}
      </button>
    );
  };
  return (
    <span className="inline-flex flex-none border border-[#E2E8F0] rounded-lg overflow-hidden">
      {cell("card", <GridGlyph />, "Card view")}
      {cell("list", <ListGlyph />, "List view")}
    </span>
  );
};

// N2-1s skeleton card — static bones, exact artboard geometry: pill bones
// 96×20 (#EEF0F3) + 76×20 (#F3F4F6), title 150×16, meta 220×12, footer
// divider + 32px avatar bone + 120×12 line. No shimmer (none specified).
export const CardSkeleton = ({ faded = false }) => (
  <div
    data-testid="cn-card-skeleton"
    className={`bg-white border border-[#E5E7EB] rounded-xl p-4 flex flex-col gap-3 ${
      faded ? "opacity-60" : ""
    }`}
  >
    <div className="flex justify-between">
      <div className="w-24 h-5 rounded-full bg-[#EEF0F3]" />
      <div className="w-[76px] h-5 rounded-full bg-[#F3F4F6]" />
    </div>
    <div className="w-[150px] h-4 rounded-[5px] bg-[#EEF0F3]" />
    <div className="w-[220px] max-w-full h-3 rounded-[5px] bg-[#F3F4F6]" />
    <div className="border-t border-[#F3F4F6] pt-3 flex items-center gap-[9px]">
      <div className="w-8 h-8 rounded-full bg-[#EEF0F3]" />
      <div className="w-[120px] h-3 rounded-[5px] bg-[#F3F4F6]" />
    </div>
  </div>
);

const FILTER_CHIP_LABELS = {
  position: (v) => `Position: ${formatPositionValue(v)}`,
  country: (v) => `Country: ${v}`,
  type_of_tender: (v) => `Type: ${v}`,
  recruiting_for: (v) => `Level: ${v === "youth" ? "Youth" : "Professional"}`,
};

const Pagination = ({ pagination, onPage }) => {
  if (!pagination || pagination.count <= 0) return null;
  const { currentPage, next, previous } = pagination;
  if (!next && !previous) return null;
  return (
    <div className="flex justify-center items-center gap-4 mt-8">
      <button
        disabled={!previous}
        onClick={() => onPage(currentPage - 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Previous
      </button>
      <span className="text-sm text-gray-600">Page {currentPage}</span>
      <button
        disabled={!next}
        onClick={() => onPage(currentPage + 1)}
        className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  );
};

const ClubNetworkFeed = () => {
  const navigate = useNavigate();
  const {
    requests,
    loading,
    error,
    showFilterModal,
    setShowFilterModal,
    filters,
    applyFilters,
    removeFilter,
    hasActiveFilters,
    clearFilters,
    searchTerm,
    setSearchTerm,
    pagination,
    fetchRequests,
    sort,
    changeSort,
    category,
    tabCounts,
    changeCategory,
    savedOnly,
    toggleSavedOnly,
    toggleSaved,
  } = useClubNetworkFeed();

  const [viewMode, setViewMode] = useViewMode("clubNetworkViewMode");
  const [messaging, setMessaging] = React.useState(null);

  // Country option list for the filter controls, distinct from the loaded
  // feed (no extra endpoint). Anonymity-safe — only the coarse country name.
  const countryOptions = useMemo(() => {
    const countries = new Map();
    requests.forEach((r) => {
      if (r.country?.name) countries.set(r.country.name, r.country.name);
    });
    return [...countries.values()].map((v) => ({ value: v, label: v }));
  }, [requests]);

  const activeChips = Object.keys(FILTER_CHIP_LABELS).filter((k) => filters[k]);

  const openChat = (request) => {
    if (request.chat_channel_id) navigate(`/inbox?channel=${request.chat_channel_id}`);
    else navigate("/inbox");
  };

  // Toolbar filter option sets — same values the filter modal drives, now
  // ALSO reachable from the artboard's toolbar dropdowns (same applyFilters
  // handler; the modal stays as the existing all-in-one entry, flagged OPEN).
  const positionOptions = [
    { value: "", label: "All positions" },
    ...getPositionsForCategory("player"),
  ];
  const countryOpts = [{ value: "", label: "All countries" }, ...countryOptions];

  const searchField = (
    <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-white border border-[#E5E7EB] rounded-lg px-3 py-[9px] max-[767px]:bg-[#F3F4F6] max-[767px]:rounded-[10px] max-[767px]:py-2.5">
      <MagnifierGlyph size={14} className="flex-none" />
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search position, country, agency…"
        className="w-full bg-transparent text-[13px] max-[767px]:text-[13.5px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none"
      />
    </div>
  );

  // The three backend-supported artboard filters (the artboard's fourth,
  // "Window", has no backend param — not wired, see FUNCTION-TODO.md).
  const filterDropdowns = (variant) => (
    <>
      <CnFilterDropdown
        label="Position"
        variant={variant}
        options={positionOptions}
        value={filters.position || ""}
        onChange={(v) => applyFilters({ ...filters, position: v })}
      />
      <CnFilterDropdown
        label="Country"
        variant={variant}
        options={countryOpts}
        value={filters.country || ""}
        onChange={(v) => applyFilters({ ...filters, country: v })}
      />
      <CnFilterDropdown
        label={variant === "pill" ? "Type" : "Request type"}
        variant={variant}
        options={FEED_TYPE_FILTER_CHOICES}
        value={filters.type_of_tender || ""}
        onChange={(v) => applyFilters({ ...filters, type_of_tender: v })}
      />
    </>
  );

  return (
    <div className="min-h-full bg-[#F8FAFC]">
      <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 pt-[18px] pb-24 max-[767px]:pt-0">
        {/* Page head — M1 renders it as the white header band (title 16px,
            gray search, scrollable filter pills); desktop per N2-1. */}
        <div className="flex flex-col gap-2.5 max-[767px]:bg-white max-[767px]:border-b max-[767px]:border-[#E5E7EB] max-[767px]:-mx-4 max-[767px]:px-4 max-[767px]:pt-3.5 max-[767px]:pb-3">
          <div className="flex flex-col gap-1">
            <h1 className="text-[18px] max-[767px]:text-[16px] font-bold text-[#111827]">
              Agent Requests
            </h1>
            {/* M1 carries no subline (binding board) — the anonymity copy
                stays on the detail page and desktop head. */}
            <p className="hidden md:block text-[12.5px] text-[#6B7280]">
              Transfer needs posted by agents on behalf of clubs. The club stays
              anonymous. The agent is your counterpart.
            </p>
          </div>

          {/* Toolbar (N2-1): search + Position/Country/Request type */}
          <div className="hidden md:flex items-center gap-2.5 flex-wrap">
            {searchField}
            {filterDropdowns("button")}
          </div>

          {/* M1: search field then the scrollable filter-pill row */}
          <div className="md:hidden flex flex-col gap-2.5">
            {searchField}
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              {filterDropdowns("pill")}
              {/* OPEN functions in pill form (no artboard placement) */}
              <CnFilterDropdown
                label="Level"
                variant="pill"
                options={FEED_LEVEL_CHOICES}
                value={filters.recruiting_for || ""}
                onChange={(v) => applyFilters({ ...filters, recruiting_for: v })}
              />
              <CnFilterDropdown
                label="Sort"
                variant="pill"
                options={SORT_CHOICES}
                value={sort}
                onChange={changeSort}
                allLabel="Newest first"
              />
              <button
                type="button"
                onClick={() => applyFilters({ ...filters, verified: !filters.verified })}
                aria-pressed={!!filters.verified}
                className={`flex-none inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap border ${
                  filters.verified
                    ? "bg-[#E8F1FF] border-[#BFDBFE] text-[#1B6FE0]"
                    : "bg-white border-[#E5E7EB] text-[#111827]"
                }`}
              >
                Verified only
              </button>
              <button
                type="button"
                onClick={toggleSavedOnly}
                aria-pressed={savedOnly}
                className={`flex-none inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap border ${
                  savedOnly
                    ? "bg-[#E8F1FF] border-[#BFDBFE] text-[#1B6FE0]"
                    : "bg-white border-[#E5E7EB] text-[#111827]"
                }`}
              >
                <BookmarkGlyph saved={savedOnly} size={11} />
                Saved
              </button>
              <button
                type="button"
                onClick={() => setShowFilterModal(true)}
                className="flex-none inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap bg-white border border-[#E5E7EB] text-[#111827]"
              >
                <FunnelGlyph size={12} />
                Filters
              </button>
            </div>
          </div>
        </div>

        {/* Category pills + Cards/List toggle — existing functions kept at
            best fit (OPEN — no artboard placement), V2 vocabulary. */}
        <CategoryPills
          activeCategory={category}
          onChange={changeCategory}
          counts={tabCounts}
          className="mt-3.5"
        >
          <FeedViewToggle value={viewMode} onChange={setViewMode} />
        </CategoryPills>

        {/* Remaining desktop tools (OPEN — no artboard placement): Verified
            only, Level, Sort, Saved, Filters modal. V2 toolbar vocabulary. */}
        <div className="hidden md:flex items-center gap-2.5 flex-wrap mt-3">
          <label className="inline-flex items-center gap-[7px] text-[12.5px] text-[#334155] cursor-pointer select-none whitespace-nowrap">
            <input
              type="checkbox"
              checked={!!filters.verified}
              onChange={() => applyFilters({ ...filters, verified: !filters.verified })}
              className="w-[15px] h-[15px] rounded border-[1.5px] border-[#CBD5E1] accent-[#2283FF]"
            />
            Verified only
          </label>
          <CustomDropdown
            options={FEED_LEVEL_CHOICES}
            value={filters.recruiting_for || ""}
            onChange={(v) => applyFilters({ ...filters, recruiting_for: v })}
            placeholder="All levels"
            displayProperty="label"
            valueProperty="value"
            {...v2DropdownProps(false)}
          />
          <CustomDropdown
            options={SORT_CHOICES}
            value={sort}
            onChange={changeSort}
            displayProperty="label"
            valueProperty="value"
            {...v2DropdownProps(false)}
          />
          <button
            type="button"
            onClick={toggleSavedOnly}
            aria-pressed={savedOnly}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[9px] text-[12.5px] font-semibold border ${
              savedOnly
                ? "bg-[#E8F1FF] border-[#BFDBFE] text-[#1B6FE0]"
                : "bg-white border-[#E2E8F0] text-[#334155] hover:bg-gray-50"
            }`}
          >
            <BookmarkGlyph saved={savedOnly} size={12} />
            Saved
          </button>
          <button
            onClick={() => setShowFilterModal(true)}
            className="inline-flex items-center gap-[7px] h-9 border border-[#E2E8F0] rounded-[9px] bg-white px-3 text-[12.5px] font-semibold text-[#334155] hover:bg-gray-50"
          >
            <FunnelGlyph size={13} />
            Filters
          </button>
        </div>

        {/* Active filter chips (N2-1f) — removable ×-disc chips + Clear all */}
        {activeChips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3.5">
            {activeChips.map((k) => (
              <RemovableFilterChip
                key={k}
                onRemove={() => removeFilter(k)}
                removeLabel={`Remove ${k} filter`}
              >
                {FILTER_CHIP_LABELS[k](filters[k])}
                {/* Country chip carries the platform image flag (Rule 3 —
                    the artboard's emoji flag is a placeholder). */}
                {k === "country" && (
                  <FlagImg
                    country={filters[k]}
                    className="w-4 h-[11px] rounded-[2px]"
                  />
                )}
              </RemovableFilterChip>
            ))}
            <button
              onClick={clearFilters}
              className="text-[12px] font-semibold text-[#2283FF] ml-auto"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Count line — "N requests · verified first" (ordering is server-
            side; the hook never re-sorts). */}
        <p className="text-[12px] text-[#6B7280] mt-3.5 mb-3.5">
          <strong className="font-bold text-[#111827]">
            {pagination.count} {pagination.count === 1 ? "request" : "requests"}
          </strong>
          {" · verified first"}
        </p>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 gap-4 md:gap-[18px] xl:gap-5 w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <CardSkeleton key={i} faded={i >= 2} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button
              onClick={() => fetchRequests(1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Try again
            </button>
          </div>
        ) : requests.length === 0 ? (
          /* N2-1e — empty card: 50px #F3F4F6 disc + S3 19×19, 15px/700
             title, 12.5px copy, secondary "Clear filters".
             ADDENDUM P9c: every new tab/filter combination gets its OWN empty
             state. The Saved filter is one of those — the generic filter-mismatch
             line was wrong there, because nothing is filtered out: nothing is
             saved yet. Copy + shape follow the marketplace's saved-empty twin
             (AgentTenderFeed.js:235-246) so the two read as one platform. */
          <div className="max-w-[440px] mx-auto mt-6 bg-white border border-[#D6DBE3] rounded-xl shadow-[0_8px_24px_rgba(17,24,39,0.08)] px-8 py-[38px] flex flex-col items-center gap-[11px] text-center">
            <div className="w-[50px] h-[50px] rounded-full bg-[#F3F4F6] flex items-center justify-center">
              {savedOnly ? <BookmarkGlyph size={19} /> : <MagnifierGlyph size={19} />}
            </div>
            <h3 className="text-[15px] font-bold text-[#111827]">
              {savedOnly
                ? "No saved requests yet"
                : "No agent requests match your filters"}
            </h3>
            <p className="text-[12.5px] text-[#6B7280] leading-[1.55]">
              {savedOnly
                ? "Tap the bookmark on any request to keep it here. Saved requests notify you when they get verified or superseded."
                : "Try removing a filter — new requests are posted every week."}
            </p>
            {savedOnly ? (
              <button
                onClick={toggleSavedOnly}
                className="mt-1 bg-white text-[#111827] border border-[#E5E7EB] rounded-lg text-[12.5px] font-semibold px-3.5 py-[9px] hover:bg-[#F3F4F6]"
              >
                Browse all requests
              </button>
            ) : (
              hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-1 bg-white text-[#111827] border border-[#E5E7EB] rounded-lg text-[12.5px] font-semibold px-3.5 py-[9px] hover:bg-[#F3F4F6]"
                >
                  Clear filters
                </button>
              )
            )}
          </div>
        ) : viewMode === "list" ? (
          <>
            <div className="flex flex-col gap-2">
              {requests.map((r) => (
                <AgentRequestListRow
                  key={r.id}
                  request={r}
                  onMessage={setMessaging}
                  onOpenChat={openChat}
                  onToggleSave={toggleSaved}
                />
              ))}
            </div>
            <Pagination pagination={pagination} onPage={(p) => fetchRequests(p)} />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 gap-4 md:gap-[18px] xl:gap-5 w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto items-start">
              {requests.map((r) => (
                <ClubRequestCard
                  key={r.id}
                  request={r}
                  onMessage={setMessaging}
                  onOpenChat={openChat}
                  onToggleSave={toggleSaved}
                />
              ))}
            </div>
            <Pagination pagination={pagination} onPage={(p) => fetchRequests(p)} />
          </>
        )}
      </div>

      <ClubNetworkFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={applyFilters}
        initialFilters={filters}
        countryOptions={countryOptions}
      />

      <MessageAgentModal
        request={messaging}
        onClose={() => setMessaging(null)}
        onSent={() => {
          setMessaging(null);
          fetchRequests(pagination.currentPage);
        }}
      />
    </div>
  );
};

export default ClubNetworkFeed;
